import {
  Box,
  Grid,
  List,
  ListItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useAppSelector } from 'app/hooks';
import AnalyzerSnapshotsSettings from 'features/snapshots/SnapshotsSettings';
import {
  appendUnitsToNumber,
  convertDBNumber,
  convertToImperial,
  getUnitForMeasurementType,
} from 'features/weather/utils/dataConverters';
import { useEffect, useState } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { TraceHistorySample, WeatherConstraint } from 'types';
import { HourlyData } from 'types/models/ForecastDto';
import CurrentConditions from 'features/weather/CurrentConditions';
import { useGetHistoricalWeatherConditions } from './hooks/useGetHistoricalWeatherConditions';
import { colorCell } from './utils/cellFormatters';
import { useLocation } from 'features/locations/api/getLocation';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { formatInTimeZone } from 'date-fns-tz';

interface Props {
  isSubmitted: boolean;
  hourlyForecasts: HourlyData[];
  currentTrace: TraceHistorySample | undefined;
  watch: UseFormWatch<{
    location_id: number;
    dish_id: number;
    duration: number;
    analyzerId: number;
    snapshotId: number;
  }>;
  editAlerts: boolean;
  noTraces: boolean;
  weatherConstraints: WeatherConstraint[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ height: 'calc(100% - 47px)' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ height: '100%' }} p={1}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ForecastReflectionTabs = ({
  isSubmitted,
  hourlyForecasts,
  currentTrace,
  watch,
  editAlerts,
  noTraces,
  weatherConstraints,
}: Props) => {
  const { monthAbbreviatedWordDayFormat, timeFormatNoSeconds, timezone } =
    useUserDateTime();
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedForecastIndex, setSelectedForecastIndex] = useState<
    null | number
  >(null);
  const { data: location } = useLocation({ locationId: watch('location_id') });
  const inImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const historicalWeatherConditions = useGetHistoricalWeatherConditions(
    watch('location_id'),
    currentTrace?.time,
    false
  );
  useEffect(() => {
    if (noTraces) setCurrentTab(0);
  }, [noTraces]);
  return (
    <>
      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
        <Tab value={0} label='Forecasts' />
        <Tab
          value={2}
          label='Weather'
          disabled={!isSubmitted || !currentTrace || editAlerts || noTraces}
        />
        <Tab
          value={1}
          label='Settings'
          disabled={!isSubmitted || !currentTrace || editAlerts || noTraces}
        />
      </Tabs>
      <Grid item container style={{ minHeight: '55vh', maxHeight: '70vh' }}>
        <TabPanel value={currentTab} index={2}>
          <CurrentConditions
            currentConditions={historicalWeatherConditions}
            inTraceHistory={true}
            location={location}
            analyzerId={watch('analyzerId')}
            weatherConstraints={weatherConstraints}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={0}>
          <Typography color={grey[500]} sx={{ p: '1rem' }}>
            Hourly forecasts with alerting conditions{' '}
            {`(${hourlyForecasts.length})`}
          </Typography>
          <Grid item wrap='nowrap' container style={{ height: '100%' }}>
            <Grid item xs={4} style={{ height: '100%', minWidth: '12rem' }}>
              <List style={{ height: '100%', overflow: 'auto' }}>
                {hourlyForecasts.map((forecast, idx) => (
                  <ListItem
                    onClick={() => setSelectedForecastIndex(idx)}
                    button
                    key={idx}
                  >
                    {formatInTimeZone(
                      new Date(forecast.time),
                      timezone,
                      `${monthAbbreviatedWordDayFormat} ${timeFormatNoSeconds}`
                    )}
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs>
              {selectedForecastIndex !== null &&
                hourlyForecasts[selectedForecastIndex] && (
                  <Grid item>
                    <TableContainer>
                      <Table aria-label='simple table'>
                        <TableBody>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell component='th' scope='row'>
                              Condition
                            </TableCell>
                            <TableCell>
                              {
                                hourlyForecasts[selectedForecastIndex]
                                  ?.condition
                              }
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(weatherConstraints, 'temp_c')}
                            >
                              Temperature
                            </TableCell>
                            <TableCell
                              sx={colorCell(weatherConstraints, 'temp_c')}
                            >
                              {convertDBNumber(
                                convertToImperial(
                                  hourlyForecasts[selectedForecastIndex].temp_c,
                                  'temp_c',
                                  inImperial
                                ),
                                getUnitForMeasurementType('temp_c', inImperial)
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(weatherConstraints, 'wind_kph')}
                            >
                              Wind Speed
                            </TableCell>
                            <TableCell
                              sx={colorCell(weatherConstraints, 'wind_kph')}
                            >
                              {convertDBNumber(
                                convertToImperial(
                                  hourlyForecasts[selectedForecastIndex]
                                    .wind_kph,
                                  'wind_kph',
                                  inImperial
                                ),
                                getUnitForMeasurementType(
                                  'wind_kph',
                                  inImperial
                                )
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(weatherConstraints, 'gust_kph')}
                            >
                              Wind Gust
                            </TableCell>
                            <TableCell
                              sx={colorCell(weatherConstraints, 'gust_kph')}
                            >
                              {convertDBNumber(
                                convertToImperial(
                                  hourlyForecasts[selectedForecastIndex]
                                    .gust_kph,
                                  'gust_kph',
                                  inImperial
                                ),
                                getUnitForMeasurementType(
                                  'gust_kph',
                                  inImperial
                                )
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(
                                weatherConstraints,
                                'cloud_percent'
                              )}
                            >
                              Cloud Cover Percentage
                            </TableCell>
                            <TableCell
                              sx={colorCell(
                                weatherConstraints,
                                'cloud_percent'
                              )}
                            >
                              {appendUnitsToNumber(
                                hourlyForecasts[selectedForecastIndex]
                                  .cloud_percent,
                                '%'
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(weatherConstraints, 'dewpoint_c')}
                            >
                              Dewpoint
                            </TableCell>
                            <TableCell
                              sx={colorCell(weatherConstraints, 'dewpoint_c')}
                            >
                              {convertDBNumber(
                                convertToImperial(
                                  hourlyForecasts[selectedForecastIndex]
                                    .dewpoint_c,
                                  'dewpoint_c',
                                  inImperial
                                ),
                                getUnitForMeasurementType(
                                  'dewpoint_c',
                                  inImperial
                                )
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(weatherConstraints, 'humidity')}
                            >
                              Relative Humidity
                            </TableCell>
                            <TableCell
                              sx={colorCell(weatherConstraints, 'humidity')}
                            >
                              {appendUnitsToNumber(
                                hourlyForecasts[selectedForecastIndex].humidity,
                                '%'
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(weatherConstraints, 'precip_mm')}
                            >
                              Precipitation
                            </TableCell>
                            <TableCell
                              sx={colorCell(weatherConstraints, 'precip_mm')}
                            >
                              {convertDBNumber(
                                convertToImperial(
                                  hourlyForecasts[selectedForecastIndex]
                                    .precip_mm,
                                  'precip_mm',
                                  inImperial
                                ),
                                getUnitForMeasurementType(
                                  'precip_mm',
                                  inImperial
                                )
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell
                              component='th'
                              scope='row'
                              sx={colorCell(weatherConstraints, 'pressure_mb')}
                            >
                              Barometric Pressure
                            </TableCell>
                            <TableCell
                              sx={colorCell(weatherConstraints, 'pressure_mb')}
                            >
                              {convertDBNumber(
                                convertToImperial(
                                  hourlyForecasts[selectedForecastIndex]
                                    .pressure_mb,
                                  'pressure_mb',
                                  inImperial
                                ),
                                getUnitForMeasurementType(
                                  'pressure_mb',
                                  inImperial
                                )
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          {currentTrace && (
            <AnalyzerSnapshotsSettings
              selectedAnalyzerId={watch('analyzerId')}
              selectedSnapshot={currentTrace}
              setSelectedSnapshotId={() => {}}
              displayActiveSnapshotSelector={false}
              editDisabled={true}
              inDialog={false}
              inHistoryMode={true}
            />
          )}
        </TabPanel>
      </Grid>
    </>
  );
};

export default ForecastReflectionTabs;
